/**
 * PROOF OF WALLET CONTROL — the product's thesis, on the surface that ships.
 *
 * `packages/core/attest.ts` has carried a complete, independently-verified implementation of this
 * since Phase 4.5: four bindings, and a Python verifier that shares none of our code agreeing on
 * 14 cases. None of it was reachable from the app. The engine could prove control and the product
 * could not, which is the widest gap a codebase can have — a capability nobody can NAME is a
 * capability nobody has.
 *
 * This module is the app-side logic, kept pure and separate from the DOM so it can be tested the
 * way the engine is. It covers the three roles the ceremony actually has:
 *
 *   ISSUE   — a counterparty asks "prove you control this wallet", and supplies the randomness.
 *             The nonce MUST come from the challenger, never the prover, or the prover can
 *             pre-compute a proof and the exercise means nothing.
 *   ANSWER  — the holder takes the exact message to the wallet that holds the key, signs it
 *             there, and pastes the signature back. **No private key ever enters this page.**
 *             That is not a limitation to work around later; it is the reason this surface is
 *             safe to use at all.
 *   VERIFY  — anyone checks a bundle. Needs no descriptor of your own, no account and no keys,
 *             because the bundle carries the descriptor and the verdict re-derives everything.
 *
 * Verification is deliberately the half that needs nothing: the person who most needs to check a
 * claim of control is the one with no relationship to the wallet.
 */
import {
  challengeMessage, verifyControlProof, nonceFromBytes, findByAddress, DescriptorError,
  resolveClass,
  type ControlChallenge, type ControlProof, type ControlVerdict, type KeySignature,
  type ParsedDescriptor, type ScanWindow,
} from '@signingroom/core';

/** How long a challenge stays answerable, by default. */
export const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * 16 bytes of browser randomness.
 *
 * `crypto.getRandomValues`, not `Math.random()`. The nonce is the ONLY thing standing between a
 * fresh proof and a replayed one, and `Math.random` is seeded, predictable and explicitly not
 * for this. `attest.ts` refuses a short nonce for the same reason.
 */
export function freshNonce(): string {
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  return nonceFromBytes(b);
}

export interface IssueOptions {
  vaultId: string;
  descriptorChecksum: string;
  challenge: string;
  now: number;
  ttlMs?: number;
}

export function issueChallenge(o: IssueOptions): ControlChallenge {
  return {
    vaultId: o.vaultId,
    descriptorChecksum: o.descriptorChecksum,
    challenge: o.challenge,
    nonce: freshNonce(),
    issuedAt: o.now,
    expiresAt: o.now + (o.ttlMs ?? DEFAULT_TTL_MS),
  };
}

/**
 * The exact bytes to sign, for pasting into a wallet's "sign message" box.
 *
 * Re-exported rather than reimplemented. Two renderings of the signed message is the one
 * duplication this feature cannot survive: a message that differs by a byte from what the
 * verifier reconstructs produces "signature did not match" on an honest, correctly-signed proof,
 * and the holder has no way to see why.
 */
export const messageToSign = (c: ControlChallenge): string => challengeMessage(c);

export class ControlInputError extends Error {
  constructor(message: string) { super(message); this.name = 'ControlInputError'; }
}

/** Parse pasted JSON into an object, with an error a human can act on. */
function parseJson(text: string, what: string): unknown {
  const t = text.trim();
  if (!t) throw new ControlInputError(`Paste the ${what} first — this box is empty.`);
  try { return JSON.parse(t); }
  catch {
    throw new ControlInputError(
      `That does not look like a ${what}. It should start with { and end with } — if you copied ` +
      `it from an email, check that nothing was cut off at either end.`);
  }
}

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Read a challenge that somebody else issued.
 *
 * Shape-checked here rather than left to `challengeMessage` to throw, because the message this
 * produces is read by the person who has to fix it. "expiresAt must be a number" is actionable;
 * a stack trace from a hashing function is not.
 */
export function readChallenge(text: string): ControlChallenge {
  const v = parseJson(text, 'challenge');
  if (!isObj(v)) throw new ControlInputError('A challenge must be a single JSON object.');
  const need = ['vaultId', 'descriptorChecksum', 'challenge', 'nonce'] as const;
  for (const k of need)
    if (typeof v[k] !== 'string' || !v[k])
      throw new ControlInputError(`This challenge is missing "${k}", so it cannot be answered.`);
  for (const k of ['issuedAt', 'expiresAt'] as const)
    if (typeof v[k] !== 'number' || !Number.isFinite(v[k]))
      throw new ControlInputError(`This challenge is missing a valid "${k}".`);
  return v as unknown as ControlChallenge;
}

/**
 * A signature as the holder pastes it: the base64 blob, and the ADDRESS it was made at.
 *
 * The address, not a branch and an index. A wallet signs a message with the key for one
 * address, and the verifier has to know which one — but the first version of this screen asked
 * for derivation coordinates, which nobody outside wallet development knows, while every wallet
 * puts the address right on its signing screen. Asking for the thing the person can see, and
 * finding the coordinates ourselves, removes the one step in the ceremony that needed insider
 * knowledge — and the commonest way an honest signature got refused.
 */
export interface PastedSignature {
  signature: string;
  address: string;
  /** Which descriptor key slot. Always 0 for a single-key wallet. */
  keyIndex?: number;
}

/**
 * The words the rest of the page uses for a network. "testnet" is the engine's name; the wallet
 * badge one panel up says "Test coins, not real money", and a refusal that switches vocabulary
 * mid-screen reads as a different product talking.
 */
const NETWORK_WORDS: Record<ParsedDescriptor['network'], string> = {
  mainnet: 'real-bitcoin', testnet: 'test-coin',
};
const ADDRESS_STARTS: Record<ParsedDescriptor['network'], string> = {
  mainnet: ' (they start with bc1 or 3)', testnet: ' (they start with tb1 or 2)',
};

/**
 * Could this text be a BIP-137 message signature? 65 bytes of base64 — a header byte in the
 * compressed range, then r and s — which is 88 characters with one "=" of padding. Decoding is
 * enough to say yes or no; the verifier decides whether it is the RIGHT signature.
 */
export function looksLikeMessageSignature(text: string): boolean {
  const t = text.replace(/\s+/g, '');
  if (!/^[A-Za-z0-9+/]+=*$/.test(t)) return false;
  let bytes: Uint8Array;
  try { bytes = Uint8Array.from(atob(t), (ch) => ch.charCodeAt(0)); }
  catch { return false; }
  return bytes.length === 65 && bytes[0]! >= 27 && bytes[0]! <= 42;
}

/**
 * The address the holder should sign with: the wallet's first receiving address.
 *
 * Step 2 used to say "pick the address shown above" and show nothing, and step 3 then asked for
 * "the address you signed with" — the one question in the ceremony a holder could not answer
 * from the screen. Any address in the scan window verifies; the first one is on every wallet's
 * receive screen, so it is the one to name.
 */
export function addressToSignWith(parsed: ParsedDescriptor): string {
  return resolveClass(parsed.payload).derive(parsed, 0, 0).address;
}

export function assembleProof(
  descriptor: string, parsed: ParsedDescriptor, window: ScanWindow,
  challenge: ControlChallenge, sigs: readonly PastedSignature[],
): ControlProof {
  if (sigs.length === 0)
    throw new ControlInputError('Add at least one signature before building the proof.');
  const signatures: KeySignature[] = sigs.map((s) => {
    const sig = s.signature.trim();
    if (!sig) throw new ControlInputError('Paste the signature from your wallet first.');
    if (!s.address.trim())
      throw new ControlInputError('Paste the address you signed with — your wallet shows it ' +
        'next to the signature.');
    let found;
    try { found = findByAddress(parsed, s.address, window); }
    catch (e) {
      if (e instanceof DescriptorError && e.code === 'BAD_ADDRESS')
        throw new ControlInputError(
          `That does not look like a ${NETWORK_WORDS[parsed.network]} address` +
          `${ADDRESS_STARTS[parsed.network]}. Copy it from the wallet's signing screen, whole.`);
      throw e;
    }
    if (!found)
      throw new ControlInputError(
        'That address is not one of the first ' +
        `${(window.lastUsed[0] ?? 0) + window.gapLimit + 1} this wallet would use. Either it ` +
        'belongs to a different wallet, or the wallet has used more addresses than this page ' +
        'is looking through — raise the range under Advanced in Your wallet.');
    // The SHAPE is checked here, before a bundle exists. The first version built a proof around
    // whatever was in the box — the literal text "not a signature" included — and offered "Send
    // this" beneath it. Verification only ran if the holder pressed "check it myself first", so
    // a person following the numbered steps sent a broken proof and heard about it from the
    // recipient. This is not the verifier: it only says whether the text could be a signature.
    if (!looksLikeMessageSignature(sig))
      throw new ControlInputError(
        'That is not a signature. A wallet\u2019s message signature is a single line of about ' +
        '88 letters, numbers, + and /, usually ending in =. Copy it whole from the wallet\u2019s ' +
        'signing screen, with nothing added.');
    return {
      keyIndex: s.keyIndex ?? 0,
      branch: found.branch,
      index: found.index,
      scheme: 'bip137',
      signature: sig,
    };
  });
  return { challenge, descriptor, signatures };
}

/** Serialise a bundle for sending. Stable key order so two copies of one proof compare equal. */
export const proofToJson = (p: ControlProof): string => JSON.stringify(p, null, 2);
export const challengeToJson = (c: ControlChallenge): string => JSON.stringify(c, null, 2);

export function readProof(text: string): ControlProof {
  const v = parseJson(text, 'proof');
  if (!isObj(v)) throw new ControlInputError('A proof must be a single JSON object.');
  if (typeof v.descriptor !== 'string' || !v.descriptor)
    throw new ControlInputError(
      'This proof carries no descriptor, so there is nothing to check the signatures against.');
  if (!isObj(v.challenge))
    throw new ControlInputError('This proof carries no challenge, so it answers nothing.');
  if (!Array.isArray(v.signatures))
    throw new ControlInputError('This proof carries no signatures.');
  return v as unknown as ControlProof;
}

export interface CheckResult {
  verdict: ControlVerdict | null;
  /** Set when the text could not be read at all — a different failure from "did not verify". */
  inputError: string | null;
}

/**
 * Check a pasted proof.
 *
 * The two failures are kept apart on purpose. "I could not read this file" is the reader's
 * problem and is fixed by pasting it again; "this does not prove control" is a VERDICT about the
 * claim, and collapsing them into one red box would let a malformed paste read as a failed proof
 * — which is exactly backwards, since an attacker would rather you saw a parse error than a
 * refusal.
 */
export function checkProof(text: string, now: number): CheckResult {
  let proof: ControlProof;
  try { proof = readProof(text); }
  catch (e) { return { verdict: null, inputError: (e as Error).message }; }
  try { return { verdict: verifyControlProof(proof, now), inputError: null }; }
  catch (e) { return { verdict: null, inputError: (e as Error).message }; }
}
